import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import axios from 'axios';
import { Brain, Play, Pause, Check, Clock, Wind, Eye, Volume2, VolumeX, SkipForward, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const typeIcons = {
  meditation: Brain,
  visualization: Eye,
  breathing: Wind
};

const typeColors = {
  meditation: 'text-secondary bg-secondary/20',
  visualization: 'text-primary bg-primary/20',
  breathing: 'text-accent bg-accent/20'
};

export default function MeditationPage() {
  const { getAuthHeader, API } = useAuth();
  const { i18n } = useTranslation();
  const [meditations, setMeditations] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [currentInstructionIndex, setCurrentInstructionIndex] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchMeditations();
    fetchProgress();
  }, [i18n.language]);

  useEffect(() => {
    let interval;
    if (activeSession && timeRemaining > 0 && !isPaused) {
      interval = setInterval(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0 && activeSession) {
      completeSession();
    }
    return () => clearInterval(interval);
  }, [activeSession, timeRemaining, isPaused]);

  // Cycle through instructions
  useEffect(() => {
    let instructionInterval;
    if (activeSession && !isPaused && activeSession.instructions) {
      const instructionDuration = (activeSession.duration * 60) / activeSession.instructions.length;
      instructionInterval = setInterval(() => {
        setCurrentInstructionIndex(prev => 
          prev < activeSession.instructions.length - 1 ? prev + 1 : prev
        );
      }, instructionDuration * 1000);
    }
    return () => clearInterval(instructionInterval);
  }, [activeSession, isPaused]);

  // Handle audio
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
      if (activeSession && !isPaused) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [activeSession, isPaused, volume, isMuted]);

  const fetchMeditations = async () => {
    const lang = i18n.language === 'no' ? 'no' : 'en';
    try {
      const response = await axios.get(`${API}/meditations?lang=${lang}`, { headers: getAuthHeader() });
      setMeditations(response.data);
    } catch (error) {
      console.error('Failed to fetch meditations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/meditations/progress`, { headers: getAuthHeader() });
      setProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const startSession = (meditation) => {
    setActiveSession(meditation);
    setTimeRemaining(meditation.duration * 60);
    setIsPaused(false);
    setCurrentInstructionIndex(0);
  };

  const completeSession = async () => {
    try {
      const response = await axios.post(
        `${API}/meditations/complete`,
        { exercise_id: activeSession.id, duration: activeSession.duration, completed: true },
        { headers: getAuthHeader() }
      );
      toast.success('Session completed! Great work.');
      
      // Check for new achievements
      if (response.data.new_achievements && response.data.new_achievements.length > 0) {
        response.data.new_achievements.forEach(achievement => {
          toast.success(`🏆 Achievement Unlocked: ${achievement.title}!`, {
            description: achievement.description,
            duration: 5000
          });
        });
      }
      
      fetchProgress();
    } catch (error) {
      console.error('Failed to complete session:', error);
    } finally {
      setActiveSession(null);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCompletionCount = (exerciseId) => {
    return progress.filter(p => p.exercise_id === exerciseId).length;
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="meditation-page">
        {/* Header */}
        <div>
          <h1 className="font-serif font-bold text-2xl md:text-3xl text-foreground">Meditation Library</h1>
          <p className="text-muted-foreground">Guided exercises with ambient audio to sharpen your mental edge</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50 border-white/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Sessions</p>
              <p className="font-serif text-2xl font-bold text-foreground">{progress.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-white/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Minutes Practiced</p>
              <p className="font-serif text-2xl font-bold text-foreground">
                {progress.reduce((acc, p) => acc + p.duration, 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Meditation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {meditations.map((meditation, index) => {
            const Icon = typeIcons[meditation.type] || Brain;
            const colorClass = typeColors[meditation.type] || 'text-secondary bg-secondary/20';
            const completions = getCompletionCount(meditation.id);
            const isLocked = meditation.locked;

            return (
              <motion.div
                key={meditation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`border-white/5 transition-all duration-300 group ${isLocked ? 'bg-card/30 opacity-75' : 'bg-card/50 hover:border-primary/30'}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-xl ${isLocked ? 'bg-muted/50' : colorClass}`}>
                        {isLocked ? <Lock className="h-6 w-6 text-muted-foreground" /> : <Icon className="h-6 w-6" />}
                      </div>
                      <div className="flex items-center gap-2">
                        {isLocked && (
                          <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Crown className="h-3 w-3" /> PRO
                          </span>
                        )}
                        {meditation.audio_url && !isLocked && (
                          <Volume2 className="h-4 w-4 text-muted-foreground" />
                        )}
                        {completions > 0 && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Check className="h-4 w-4 text-secondary" />
                            {completions}x
                          </div>
                        )}
                      </div>
                    </div>
                    <h3 className={`font-serif font-semibold text-lg mb-2 ${isLocked ? 'text-muted-foreground' : 'text-foreground'}`}>{meditation.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{meditation.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {meditation.duration} min
                      </div>
                      {isLocked ? (
                        <Link to="/subscription">
                          <Button
                            size="sm"
                            className="bg-primary/20 text-primary hover:bg-primary hover:text-primary-foreground rounded-full"
                          >
                            <Crown className="h-4 w-4 mr-1" />
                            Unlock
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => startSession(meditation)}
                          className="bg-primary/20 text-primary hover:bg-primary hover:text-primary-foreground rounded-full"
                          data-testid={`start-meditation-${meditation.id}`}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Start
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Active Session Dialog */}
        <Dialog open={!!activeSession} onOpenChange={() => setActiveSession(null)}>
          <DialogContent className="glass border-primary/20 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-center">{activeSession?.title}</DialogTitle>
            </DialogHeader>
            
            {/* Hidden Audio Element */}
            {activeSession?.audio_url && (
              <audio ref={audioRef} src={activeSession.audio_url} loop />
            )}
            
            <div className="py-6 text-center space-y-6">
              {/* Timer Circle */}
              <div className="relative w-40 h-40 mx-auto">
                <svg className="w-full h-full -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="72"
                    stroke="currentColor"
                    strokeWidth="6"
                    fill="none"
                    className="text-muted"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="72"
                    stroke="currentColor"
                    strokeWidth="6"
                    fill="none"
                    className="text-primary"
                    strokeLinecap="round"
                    strokeDasharray={452}
                    strokeDashoffset={452 - (452 * (1 - timeRemaining / (activeSession?.duration * 60 || 1)))}
                    style={{ transition: 'stroke-dashoffset 1s linear' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-mono text-3xl font-bold text-foreground" data-testid="session-timer">
                    {formatTime(timeRemaining)}
                  </span>
                </div>
              </div>

              {/* Current Instruction */}
              {activeSession?.instructions && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentInstructionIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="p-4 bg-primary/10 rounded-xl border border-primary/20 min-h-[80px] flex items-center justify-center"
                  >
                    <p className="text-foreground italic text-lg" data-testid="current-instruction">
                      {activeSession.instructions[currentInstructionIndex]}
                    </p>
                  </motion.div>
                </AnimatePresence>
              )}

              {/* Volume Control */}
              {activeSession?.audio_url && (
                <div className="flex items-center justify-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsMuted(!isMuted)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                  </Button>
                  <Slider
                    value={[volume * 100]}
                    onValueChange={(v) => setVolume(v[0] / 100)}
                    max={100}
                    step={1}
                    className="w-32"
                    data-testid="volume-slider"
                  />
                </div>
              )}

              {/* Controls */}
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setIsPaused(!isPaused)}
                  className="rounded-full px-8"
                  data-testid="pause-resume-btn"
                >
                  {isPaused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                </Button>
                <Button
                  size="lg"
                  onClick={completeSession}
                  className="bg-primary text-primary-foreground rounded-full px-8"
                  data-testid="complete-session-btn"
                >
                  <Check className="h-5 w-5 mr-2" />
                  Complete
                </Button>
              </div>

              {/* Skip Instruction */}
              {activeSession?.instructions && currentInstructionIndex < activeSession.instructions.length - 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentInstructionIndex(prev => prev + 1)}
                  className="text-muted-foreground"
                >
                  <SkipForward className="h-4 w-4 mr-1" />
                  Next instruction
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
